import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";

/**
 * Layer 2 anomaly: a constant numeric cell sitting in a row-band or
 * column-band that's otherwise dominantly formulas. The canonical
 * "user typed a number on top of a calculated cell" model error —
 * the #1 source of bugs in spreadsheet financial models per the
 * architecture doc.
 *
 * This rule exists ONLY on the model substrate. The legacy SheetData
 * approach can't express it well — band-level pattern dominance needs
 * the substrate's region segmentation (Layer 1) and R1C1 interning
 * (Layer 0) to be cheap. `check` therefore returns []; the verifier
 * is told to skip the legacy-vs-model comparison via `modelOnly: true`.
 *
 * Findings are sorted by severity inside patternAnalysis.anomalies, so
 * the highest-confidence flags surface first in the Review dialog.
 */
export const hardcodeInFormulaBand: AuditRule = {
  id: "system.audit.hardcode-in-formula-band",
  name: "Hardcode in formula band",
  description:
    "A constant numeric cell embedded in a band that's otherwise dominantly formulas. " +
    "Often an unintended override of a calculated value. Band-level granularity catches " +
    "outliers the column-only inconsistentFormulas rule misses.",
  enabled: true,
  source: "system",
  severity: "warning",
  needsPatternAnalysis: true,
  check(sheet) {
    if (!sheet.patternAnalysis) return [];
    const findings: AuditFindingPartial[] = [];
    const { startRow, startCol } = sheet.bounds;

    for (const a of sheet.patternAnalysis.anomalies) {
      if (a.type !== "hardcode_in_formula_band") continue;
      findings.push({
        sheetName: sheet.name,
        address: addressFromRC(startRow + a.row, startCol + a.col),
        message: `hardcoded value in a band that's otherwise formulas — likely manual override (severity ${a.severityScore.toFixed(2)})`,
      });
    }
    return findings;
  },
};
